define('presentation/receptionist/facades/receptionistFacade',['common/promises/promiseFactory',
        'constants/receptionistMessageConstants',
        'externalDependencies/clientWebSocket',
        'presentation/common/callDetails/common/callDetailsBuilder',
        'presentation/common/callDetails/presentationObjects/callDetailsPresentationObject',
        'presentation/common/callDetails/presentationObjects/callDetailsReceptionistMessagePresentationObject',
        'common/converters/phoneNumberFormatter',
        'common/converters/userFormatter'
    ],
    function () {
        const PromiseFactoryConstructor = require('common/promises/promiseFactory');

        const receptionistMessageConstants = require('constants/receptionistMessageConstants');

        const _clientWebSocket = require('externalDependencies/clientWebSocket');
        const _callDetailsSocket = _clientWebSocket.forApplication('callDetails');
        const _receptionistMessageSocket = _clientWebSocket.forApplication('receptionistMessage');
        const _receptionistMessageSubscriptionWebSocket = _clientWebSocket.forApplication('receptionistMessageSubscriptionDataSource');

        const CallDetailsBuilderConstructor = require('presentation/common/callDetails/common/callDetailsBuilder');
        const _callDetailsBuilder = new CallDetailsBuilderConstructor();

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const UserFormatterConstructor = require('common/converters/userFormatter');
        const _userFormatter = new UserFormatterConstructor();

        const CallDetailsPresentationObject = require('presentation/common/callDetails/presentationObjects/callDetailsPresentationObject');
        const CallDetailsReceptionistMessagePresentationObject = require('presentation/common/callDetails/presentationObjects/callDetailsReceptionistMessagePresentationObject');

        let _promiseFactory = null;

        const init = (promiseFactory) => {
            _promiseFactory = promiseFactory;

            _callDetailsBuilder.init();
        };


        const _formatFormResponsesForDisplay = (formResponses) => {
            if (formResponses === null || formResponses === undefined) {
                return [];
            }

            return formResponses.map((response) => {
                const { formKey, formValue } = response;
                return {
                    key: formKey,
                    value: formValue.trim() ? formValue : "-"
                };
            });
        };

        const getCallDetails = (receptionistMessageId, callHistoryId) => {
            return _promiseFactory.deferIndefinitely((promise) => {

                let callDetails = new CallDetailsPresentationObject();
                let receptionistMessage = new CallDetailsReceptionistMessagePresentationObject();

                const messageDetailsPromiseFactory = new PromiseFactoryConstructor();

                messageDetailsPromiseFactory.deferIndefinitely((callDetailsPromise) => {
                    if (callHistoryId == null) {
                        callDetailsPromise.resolve();
                        return;
                    }

                    const callDetailsRequest = {
                        callHistoryId: callHistoryId
                    };
                    _callDetailsSocket.send('getCallDetailsByCallHistoryId', callDetailsRequest, (callDetailsResult) => {
                        callDetails = _callDetailsBuilder.formatCallDetailsForDisplay(callHistoryId, callDetailsResult);
                        const mapRequest = {
                            callerLocation: callDetailsResult.callerLocation,
                            calleeLocation: callDetailsResult.calleeLocation,
                            mapHeight: 200,
                            mapWidth: 744
                        };
                        _callDetailsSocket.send('getCallDetailsMapUrl', mapRequest, (mapResult) => {
                            if (mapResult) {
                                callDetails.mapUrl = mapResult;
                            }
                            callDetailsPromise.resolve(callDetails);
                        });
                    });
                });

                messageDetailsPromiseFactory.deferIndefinitely((messageDetailsPromise) => {
                    const request = {
                        receptionistMessageId: receptionistMessageId
                    };

                    _receptionistMessageSocket.send('getReceptionistMessage', request, (messageDetailsResult) => {
                        receptionistMessage.receptionistMessageId = messageDetailsResult.receptionistMessageId;
                        receptionistMessage.scriptName = messageDetailsResult.liveReceptionistScriptDisplayName;
                        receptionistMessage.interactionType = messageDetailsResult.interactionType;
                        receptionistMessage.employeeName = _userFormatter.formatFirstNameLastInitial(messageDetailsResult.employeeName);
                        receptionistMessage.formName = messageDetailsResult.formName;
                        receptionistMessage.isJunk = messageDetailsResult.isJunk;
                        receptionistMessage.junkStatus = messageDetailsResult.junkStatus;
                        receptionistMessage.participantName = messageDetailsResult.participantName;
                        receptionistMessage.participantEmailAddress = messageDetailsResult.participantEmailAddress;
                        receptionistMessage.domain = messageDetailsResult.domain;
                        receptionistMessage.webChatTranscript = messageDetailsResult.webChatTranscript.map((transcriptMessage) => {
                            if (transcriptMessage.isAgent) {
                                transcriptMessage.sender = _userFormatter.formatFirstNameLastInitial(transcriptMessage.sender);
                            }
                            return transcriptMessage;
                        });
                        receptionistMessage.formResponses = _formatFormResponsesForDisplay(messageDetailsResult.formResponses);
                        receptionistMessage.subscriberIds(messageDetailsResult.subscriberIds);
                        let smsRecipients = messageDetailsResult.smsRecipients || [];
                        let recipients = messageDetailsResult.recipients || [];
                        let formattedSmsRecipients = smsRecipients.map((smsRecipient) => {
                            return _phoneNumberFormatter.toInternationalWithParens(smsRecipient);
                        });
                        receptionistMessage.recipients = recipients.concat(formattedSmsRecipients).sort();
                        messageDetailsPromise.resolve();
                    });
                });

                messageDetailsPromiseFactory.wait()
                    .fail(promise.reject)
                    .done(() => {
                        if (callDetails !== null && callDetails.receptionistMessage !== null) {
                            callDetails.receptionistMessage.formResponses = receptionistMessage.formResponses;
                            callDetails.receptionistMessage.recipients = receptionistMessage.recipients;
                        }  else {
                            callDetails.receptionistMessage = receptionistMessage;
                        }
                        promise.resolve(callDetails);
                    });
            });
        };

        const deleteReceptionistMessage = (messageId) => {
            return _promiseFactory.defer((promise) => {
                const data = {
                    messageId: messageId,
                };
                // TODO
                promise.resolve();
            });
        };

        const setReceptionistMessageStatus = (receptionistMessageId, newStatus) => {
            return _promiseFactory.defer((promise) => {
                let status;
                switch (newStatus) {
                    case receptionistMessageConstants.status.new.name:
                        status = receptionistMessageConstants.status.new.id;
                        break;
                    case receptionistMessageConstants.status.reviewed.name:
                        status = receptionistMessageConstants.status.reviewed.id;
                        break;
                    default:
                        throw new Error('Unknown status: ' + newStatus);
                }

                const data = {
                    receptionistMessageId: receptionistMessageId,
                    status: status,
                };
                _receptionistMessageSocket.send("setMessageStatus", data, () => {
                    promise.resolve();
                });
            });
        };

        const getReceptionistMessages = ({ startDate, endDate, searchStatus, includeJunk, searchInteraction, selectedLiveReceptionistScriptIds, pageNumber, itemCount }) => {
            return _promiseFactory.defer((promise) => {

                let formattedStartDate = null;
                if (startDate) {
                    formattedStartDate = new Date(new Date(startDate).setHours(0, 0, 0, 0)).toISOString();
                }

                let formattedEndDate = null;
                if (endDate) {
                    formattedEndDate = new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString();
                }

                let formattedSearchStatus;
                switch (searchStatus) {
                    case receptionistMessageConstants.status.new.status:
                    case receptionistMessageConstants.status.reviewed.status:
                        formattedSearchStatus = receptionistMessageConstants.status[searchStatus].id;
                        break;
                    default:
                        formattedSearchStatus = null;
                        break;
                }

                let formattedInteractionType;
                switch (searchInteraction) {
                    case receptionistMessageConstants.interactionType.call.status:
                        formattedInteractionType = receptionistMessageConstants.interactionType.call.id;
                        break;
                    case receptionistMessageConstants.interactionType.chat.status:
                        formattedInteractionType = receptionistMessageConstants.interactionType.chat.id;
                        break;
                    default:
                        formattedInteractionType = null;
                }

                const request = {
                    startDate: formattedStartDate,
                    endDate: formattedEndDate,
                    messageStatus: formattedSearchStatus,
                    includeJunk: includeJunk,
                    messageInteractionType: formattedInteractionType,
                    liveReceptionistScriptIds: selectedLiveReceptionistScriptIds,
                    pageNumber: pageNumber,
                    itemCount: itemCount
                };

                _receptionistMessageSocket.send('initializeMessageList', request, (result) => {
                    promise.resolve(result);
                });
            });
        };

        const getFilteredReceptionistMessage = (receptionistMessageId, { startDate, endDate, searchStatus, includeJunk, searchInteraction, selectedLiveReceptionistScriptIds}) => {
            return _promiseFactory.defer((promise) => {

                let formattedStartDate = null;
                if (startDate) {
                    formattedStartDate = new Date(new Date(startDate).setHours(0, 0, 0, 0)).toISOString();
                }

                let formattedEndDate = null;
                if (endDate) {
                    formattedEndDate = new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString();
                }

                let formattedSearchStatus;
                switch (searchStatus) {
                    case receptionistMessageConstants.status.new.status:
                    case receptionistMessageConstants.status.reviewed.status:
                        formattedSearchStatus = receptionistMessageConstants.status[searchStatus].id;
                        break;
                    default:
                        formattedSearchStatus = null;
                        break;
                }

                let formattedInteractionType;
                switch (searchInteraction) {
                    case receptionistMessageConstants.interactionType.call.status:
                        formattedInteractionType = receptionistMessageConstants.interactionType.call.id;
                        break;
                    case receptionistMessageConstants.interactionType.chat.status:
                        formattedInteractionType = receptionistMessageConstants.interactionType.chat.id;
                        break;
                    default:
                        formattedInteractionType = null;
                }

                const request = {
                    receptionistMessageId: receptionistMessageId,
                    startDate: formattedStartDate,
                    endDate: formattedEndDate,
                    messageStatus: formattedSearchStatus,
                    includeJunk: includeJunk,
                    messageInteractionType: formattedInteractionType,
                    liveReceptionistScriptIds: selectedLiveReceptionistScriptIds
                };

                _receptionistMessageSocket.send('getFilteredMessages', request, (result) => {
                    const hasReceptionistMessage = result.length > 0;
                    const response = {
                        hasReceptionistMessage: hasReceptionistMessage,
                        foundReceptionistMessage: hasReceptionistMessage ? result[0] : null
                    };
                    promise.resolve(response);
                });
            });
        };

        const checkAnyReceptionistMessages = () => {
            return _promiseFactory.defer(promise => {
                _receptionistMessageSocket.send('anyMessages', {}, (result) => {
                    promise.resolve(result.anyMessages);
                });
            });
        };

        const onReceptionistMessageStatusChanged = (callback) => {
            _receptionistMessageSubscriptionWebSocket.subscribeToEvent("receptionistMessageStatusChanged", (event) => {
                callback(event.receptionistMessageId, event.newStatus);
            });
        };

        const onReceptionistMessageAdded = (callback) => {
            _receptionistMessageSubscriptionWebSocket.subscribeToEvent('receptionistMessageAdded', (event) => {
                callback(event.receptionistMessageId);
            });
        };

        const onReceptionistMessageDeleted = (callback) => {
            _receptionistMessageSubscriptionWebSocket.subscribeToEvent('receptionistMessageDeleted', (event) => {
                callback(event.receptionistMessageId);
            });
        };

        const onLiveReceptionistScriptStatusChanged = (callback) => {
            _receptionistMessageSubscriptionWebSocket.subscribeToEvent("liveReceptionistScriptStatusChanged", (eventData) => {
                callback(eventData);
            });
        };

        const onLiveReceptionistScriptSubscriberSettingsChanged = (callback) => {
            _receptionistMessageSubscriptionWebSocket.subscribeToEvent("liveReceptionistScriptSubscriberSettingsChanged", (eventData) => {
                callback(eventData);
            });
        };

        const _dispose = () => {
            _receptionistMessageSubscriptionWebSocket.disposeOfEvents();
            _receptionistMessageSocket.disposeOfEvents();
        };

        return function () {
            let self = this;

            self.init = init;
            self.checkAnyReceptionistMessages = checkAnyReceptionistMessages;
            self.deleteReceptionistMessage = deleteReceptionistMessage;
            self.dispose = _dispose;
            self.getCallDetails = getCallDetails;
            self.getReceptionistMessages = getReceptionistMessages;
            self.getFilteredReceptionistMessage = getFilteredReceptionistMessage;
            self.onReceptionistMessageAdded = onReceptionistMessageAdded;
            self.onReceptionistMessageDeleted = onReceptionistMessageDeleted;
            self.onReceptionistMessageStatusChanged = onReceptionistMessageStatusChanged;
            self.onLiveReceptionistScriptStatusChanged = onLiveReceptionistScriptStatusChanged;
            self.onLiveReceptionistScriptSubscriberSettingsChanged = onLiveReceptionistScriptSubscriberSettingsChanged;
            self.setReceptionistMessageStatus = setReceptionistMessageStatus;
        };
    }
);

